import { useState } from "react";
import styled from 'styled-components';
import { Modal, Button, LoadingIcon } from '@prosolve/common-components-ui'
import useDataTextToSpeech from '../../../dataHooks/useDataTextToSpeech';
import useSnackbar from '../../../hooks/useSnackbar';

const Header = styled.div`
  display: flex;
  align-items: center;
  color: #565656;
  font-weight: bold;
`

const Title = styled.div`
  font-size: 22px;
`

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  column-gap: 10px;
  padding: 10px;
`

const ProgressText = styled.div`
  flex: 1;
  font-size: 14px;
  font-style: italic;
  color: #565656;
  padding-left: 10px;
`

const QuestionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
`

const QuestionText = styled.div`
  font-style: italic;
  color: #565656;
  padding: 10px;
  background: #f8f9fc;
  border: 1px solid #dedede;
  border-radius: 5px;
`

export default ({
  assessmentName,
  questions = [],
  onClose,
  onSuccess
}) => {
  const snackbar = useSnackbar();

  const { generateQuestionAudio } = useDataTextToSpeech();

  const [isBusy, setIsBusy] = useState(false)

  const [progressText, setProgressText] = useState('')

  const questionsWithText = questions.filter(({ question }) => question)

  const onSubmit = async () => {
    if (isBusy) {
      return;
    }
    setIsBusy(true)

    const audioUrlsByQuestionId = {};
    let failCount = 0;

    for (let i = 0; i < questionsWithText.length; i++) {
      setProgressText(`Generating audio ${i + 1} of ${questionsWithText.length}...`)
      const { id: questionId, question: questionText } = questionsWithText[i];
      try {
        const result = await generateQuestionAudio({ assessmentName, questionId, questionText })
        if (result && result.success) {
          audioUrlsByQuestionId[questionId] = result.fileUrl;
        } else {
          failCount++;
        }
      } catch (e) {
        console.log(e)
        failCount++;
      }
    }

    if (failCount === 0) {
      snackbar.newMessage({ message: 'Audio files generated. Save the assessment to keep the audio links.', severity: 'success' })
    } else {
      snackbar.newMessage({ message: `Failed to generate audio for ${failCount} of ${questionsWithText.length} questions`, severity: 'failure' })
    }

    onSuccess(audioUrlsByQuestionId)
  }

  const TitleComponent = (
    <Header>
      <Title>
        {'Bulk Generate Question Audio'}
      </Title>
    </Header>
  )

  const baseButtonStyle = {
    padding: '10px',
    fontSize: '18px',
    borderRadius: '10px',
    width: '100px'
  }

  const FooterComponent = (
    <ButtonsContainer>
      <ProgressText>{isBusy ? progressText : ''}</ProgressText>
      <Button style={baseButtonStyle} onClick={onClose} $disabled={isBusy}>
        {'Cancel'}
      </Button>
      <Button style={{ ...baseButtonStyle, fontWeight: 'bold', position: 'relative' }} onClick={onSubmit} $colored={true} $disabled={isBusy}>
        {
          isBusy ?
          <>
            &nbsp;
            <div style={{ position: 'absolute', top: '3px' }}>
              <LoadingIcon />
            </div>
          </>
          :
          'Generate'
        }
      </Button>
    </ButtonsContainer>
  )

  return (
    <Modal
      title={TitleComponent}
      closeOnOffClick={false}
      footer={FooterComponent}
      onClose={onClose}
    >
      <Content>
        <div>{`This will generate a text-to-speech audio file for each of the ${questionsWithText.length} questions below and replace any existing audio for these questions.`}</div>
        <QuestionList>
          {
            questionsWithText.map(({ id, question }) => (
              <QuestionText key={id}>{question}</QuestionText>
            ))
          }
        </QuestionList>
        <div>{'Do you wish to proceed?'}</div>
      </Content>
    </Modal>
  )
}
